/**
 * ApiProvider Component
 *
 * Sets up the API client with the authentication token getter and
 * a session-expired handler that triggers Auth0 logout only when
 * token refresh fails (session truly expired). Must be rendered
 * inside Auth0Provider.
 */

import { useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  setTokenGetter,
  setOnSessionExpired,
  clearOnSessionExpired,
} from "../services/api";
import { setSegmentChatTokenGetter } from "../services/segmentChat";
import { setTherapistChatTokenGetter } from "../services/therapistChat";

interface ApiProviderProps {
  children: React.ReactNode;
}

export function ApiProvider({ children }: ApiProviderProps) {
  const { getAccessToken, isAuthenticated, logout } = useAuth();

  const handleSessionExpired = useCallback(() => {
    logout();
  }, [logout]);

  // Set both token getter AND session expired handler synchronously
  // during render so they're available before any child useEffect
  // callbacks (e.g. React Query fetches) fire.
  // useEffect runs children-first, so a parent useEffect would be too late.
  if (isAuthenticated) {
    setTokenGetter(getAccessToken);
    setSegmentChatTokenGetter(getAccessToken);
    setTherapistChatTokenGetter(getAccessToken);
    setOnSessionExpired(handleSessionExpired);
  }

  useEffect(() => {
    return () => {
      clearOnSessionExpired();
    };
  }, []);

  return <>{children}</>;
}
